<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, watchEffect } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import { StyledRecordings } from '@/composables/styledRecording'
import store from '@/store'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { isSung } from '@/util/RecordingHelpers'
import { READ, SING, TEXT, VoiceStyles, VoiceStyleSet } from '@/util/VoiceStyles'

interface Props {
  input?: () => void
  label?: string
  recordings?: StyledRecordings | null
  annotatedSentences?: Array<AnnotatedSentence> | null
  voiceStyleSet: VoiceStyleSet | null
  voice?: string
}

const logger = useLogger()

const props = withDefaults(defineProps<Props>(), {
  annotatedSentences: null,
  input: () => {},
  label: 'Practice',
  recordings: null,
  voice: '',
})
const emit = defineEmits(['update:voice'])

const state = reactive({
  VoiceStyles,
  selectedVoice: props.voice,
})

// computes the number of read recordings vs the number of sung recordings
const voiceStyleStats = computed<Record<string, number>>((): Record<string, number> => {
  if (!props.annotatedSentences?.length) {
    return {}
  }

  const map: Record<string, number> = {}

  // count the number of read recordings and sung recordings for this sentence
  props.annotatedSentences.forEach((annotatedSentence: AnnotatedSentence) => {
    const sentence = annotatedSentence.sentence()
    if (!store.getters.audio.hasRecordingsForSentence(sentence.id)) {
      return
    }

    store.getters.audio.getRecordingListForSentence(sentence.id).forEach((srmdwpa: SentenceRecordingMetadataWithPitchedAudios) => {
      if (!srmdwpa.sentenceRecording) {
        logger.error('unexpected null srmdwpa.sentenceRecording')
        return
      }

      const key = isSung(srmdwpa) ? VoiceStyles[SING] : VoiceStyles[READ]
      map[key] = (map[key] ?? 0) + 1
    })
  })

  // Text should always be there
  map[VoiceStyles[TEXT]] = 0
  return map
})

const voiceStyleKeys = computed(() => Object.keys(voiceStyleStats.value))

onMounted(() => {
  watchEffect(() => {
    if (voiceStyleKeys.value.length === 1) {
      logger.debug('voiceStyleStats 1')
      nextTick(() => {
        state.selectedVoice = voiceStyleKeys.value[0]
      })
    }
  })

  watchEffect(() => {
    state.selectedVoice = props.voice
  })

  watchEffect(() => {
    emit('update:voice', state.selectedVoice)
  })
})
</script>

<template>
  <div v-if="voiceStyleKeys.length" class="voice-select-group">
    <span class="text-caption text-medium-emphasis">Voice</span>
    <v-btn-toggle v-model="state.selectedVoice" color="primary" density="compact" mandatory @update:model-value="input">
      <v-btn v-for="voiceStyle in voiceStyleKeys" :key="voiceStyle" size="small" :value="voiceStyle">
        {{ voiceStyle }}<template v-if="voiceStyleStats[voiceStyle] !== 0"> ({{ voiceStyleStats[voiceStyle] }})</template>
      </v-btn>
    </v-btn-toggle>
  </div>
</template>

<style scoped>
.voice-select-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.voice-select-group :deep(.v-btn-toggle) {
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 6px;
  width: fit-content;
}
</style>
